import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ListButton, SaveButton } from "@refinedev/antd";
import {
  Collapse,
  Flex,
  Form,
  Input,
  Select,
  Result,
  Spin,
  Switch,
  Typography,
  message,
} from "antd";
import { CheckOutlined, LeftOutlined, ToolOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utilities";
import { PhoneNumberInput } from "@/components/phone-number-input";
import { getStoreSettings, updateStoreSettings } from "./api";

export const StoresSettingsList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const storeCode = decodeURIComponent(id ?? "");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  /** Ver el comentario equivalente en `pages/sellers/settings/list.tsx`: sin datos, el form no se edita. */
  const [loadError, setLoadError] = useState<"forbidden" | "error" | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!storeCode) return;
    setLoading(true);
    getStoreSettings(storeCode)
      .then((data) => form.setFieldsValue(data))
      .catch((e: any) => {
        const forbidden = e?.response?.status === 403;
        setLoadError(forbidden ? "forbidden" : "error");
        message.error(
          forbidden
            ? "No tenés permisos para ver la configuración de este store"
            : "No se pudo cargar la configuración del store",
        );
      })
      .finally(() => setLoading(false));
  }, [storeCode, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await updateStoreSettings(storeCode, values);
      message.success("Configuración guardada");
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const collapseItems = [
    {
      key: "general",
      label: <span style={{ fontWeight: 600 }}>General</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          colon
        >
          <Form.Item label="Código" name="code">
            <Input disabled />
          </Form.Item>
          <Form.Item label="Nombre del store" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="URL del store"
            name="url"
            rules={[{ required: true }]}
            extra="Para usar tu propio dominio, editá el registro CNAME WWW apuntando a host.xcons.com.ar en tu proveedor de DNS."
          >
            <Input placeholder="storename.xcons.com.ar" />
          </Form.Item>
          <Form.Item label="Plan" name="plan" rules={[{ required: true }]}>
            <Select placeholder="Elegí el plan">
              <Select.Option value="0">Store Basic</Select.Option>
              <Select.Option value="1">Store AI</Select.Option>
              <Select.Option value="2">Store VPS</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="En mantenimiento" name="status" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "contact",
      label: <span style={{ fontWeight: 600 }}>Contacto</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          colon
        >
          <Form.Item label="CUIT / Tax ID" name="tax" rules={[{ required: true }]}>
            <Input placeholder="Número de identificación fiscal" />
          </Form.Item>
          <Form.Item label="WhatsApp" name="whatsapp" rules={[{ required: true }]}>
            <PhoneNumberInput placeholder="WhatsApp" />
          </Form.Item>
          <Form.Item label="Teléfono" name="phone" rules={[{ required: true }]}>
            <Input placeholder="Número fijo u otro" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: "email" }]}
          >
            <Input placeholder="tu@email.com" />
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Flex vertical style={{ height: "100%", minHeight: 0 }}>
      {/* Es una página del menú, no un detalle: va el ícono del menú y no la flecha de volver (xcons-docs#509). */}
      <Flex align="center" gap={8} style={{ marginBottom: 24, flexShrink: 0 }}>
        <ToolOutlined style={{ fontSize: 22 }} />
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t("settings.store.title", "Configuración del Store")}
        </Typography.Title>
      </Flex>

      <div style={{ flex: 1, overflowY: "auto" }}>
        <Spin spinning={loading}>
          {loadError ? (
            <Result
              status={loadError === "forbidden" ? "403" : "error"}
              title={
                loadError === "forbidden"
                  ? "No tenés permisos para ver esta configuración"
                  : "No se pudo cargar la configuración"
              }
              subTitle={
                loadError === "forbidden"
                  ? "Pedile a un administrador del store el permiso de Operadores del Store."
                  : "Volvé a intentar en un momento."
              }
            />
          ) : (
            <Collapse defaultActiveKey={["general", "contact"]} items={collapseItems} />
          )}
        </Spin>
      </div>

      <div style={{ paddingTop: 32, paddingBottom: 16, flexShrink: 0 }}>
        <Flex justify="space-between">
          <ListButton icon={<LeftOutlined />} onClick={() => navigate(-1)}>
            {t("buttons.back", "Volver")}
          </ListButton>
          {!loadError && (
            <SaveButton loading={saving} onClick={handleSave} icon={<CheckOutlined />} iconPosition="end">
              {t("buttons.save", "Guardar")}
            </SaveButton>
          )}
        </Flex>
      </div>
    </Flex>
  );
};
